import React, { useRef } from 'react';

import { keyframes } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { PlaylistItemIcon } from '../playlist/PlaylistItemIcon';

export type AudioIconStackProps = {
	featureList: AudioFeature[];
};

const primaryTileKeyframes = keyframes({
	'0%': { transform: 'translateX(3px) rotate(-12deg)' },
	'10%': { transform: 'translateX(3px) rotate(-12deg)' },
	'40%': { transform: 'translateX(28px) rotate(0deg)' },
	'60%': { transform: 'translateX(28px) rotate(0deg)' },
	'90%': { transform: 'translateX(54px) rotate(12deg)' },
	'100%': { transform: 'translateX(54px) rotate(12deg)' },
});

const secondaryTileKeyframes = keyframes({
	'0%': { transform: 'translateX(-3px) rotate(12deg)' },
	'10%': { transform: 'translateX(-3px) rotate(12deg)' },
	'40%': { transform: 'translateX(-29px) rotate(0deg)' },
	'60%': { transform: 'translateX(-29px) rotate(0deg)' },
	'90%': { transform: 'translateX(-54px) rotate(-12deg)' },
	'100%': { transform: 'translateX(-54px) rotate(-12deg)' },
});

// Opacity change is intentionally instant to align with tile switch animation
const hideKeyframes = keyframes({
	'0%': { opacity: 1 },
	'49.99%': { opacity: 1 },
	'50%': { opacity: 0 },
	'100%': { opacity: 0 },
});

const styles = cssMap({
	stackContainer: {
		width: '119px',
		height: '68px',
	},
	centeredContainer: {
		display: 'flex',
		justifyContent: 'center',
	},
	stackLayer: {
		position: 'absolute',
	},
	iconTile: {
		height: '57px',
		width: '57px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
	},
	primaryIconTile: {
		transform: 'rotate(-12deg) translateX(3px)',
	},
	primaryIconTileSwapped: {
		transform: 'translateX(54px) rotate(12deg)',
	},
	secondaryIconTile: {
		transform: 'rotate(12deg) translateX(-3px)',
	},
	secondaryIconTileSwapped: {
		transform: 'translateX(-54px) rotate(-12deg)',
	},
	transparentIconTile: {
		opacity: 0,
	},
	scaledIcon: {
		// Necessary as the ADS Icon component only support up to 16px
		transform: 'scale(1.75)',
	},
	iconContainer: {
		position: 'relative',
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	stackedIcon: {
		position: 'absolute',
	},
	primaryIconTileAnimation: {
		animation: `${primaryTileKeyframes} 0.8s ease-in both`,
	},
	primaryIconTileAnimationReversed: {
		animation: `${primaryTileKeyframes} 0.8s ease-out both reverse`,
	},
	secondaryIconTileAnimation: {
		animation: `${secondaryTileKeyframes} 0.8s ease-in both`,
	},
	secondaryIconTileAnimationReversed: {
		animation: `${secondaryTileKeyframes} 0.8s ease-out both reverse`,
	},
	hideAnimation: {
		animation: `${hideKeyframes} 0.8s linear both`,
	},
	showAnimation: {
		animation: `${hideKeyframes} 0.8s linear both reverse`,
	},
});

//this export will only show the current playing item.
// This is useful for the mini-player which will never show the pending content's emoji
export const PlayingAudioEmojiIcon = (): React.JSX.Element => {
	const { activeContent } = useAudioPlayerStore();

	const emojiId = activeContent.emojiId;
	const contentType = activeContent.contentType;
	const subType = activeContent.subType;

	return (
		<PlaylistItemIcon
			emojiId={emojiId ?? undefined}
			contentType={contentType}
			isLive={subType === 'live'}
		/>
	);
};

//this export is for the title of the full size player where the emoji can be either
// the active or pending content
export const AudioEmojiIcon = (): React.JSX.Element => {
	const { activeContent, pendingContent } = useAudioPlayerStore();

	const emojiId = pendingContent?.emojiId ?? activeContent.emojiId;
	const contentType = pendingContent?.contentType ?? activeContent.contentType;
	const subType = pendingContent?.subType ?? activeContent.subType;

	return (
		<PlaylistItemIcon
			emojiId={emojiId ?? undefined}
			contentType={contentType}
			isLive={subType === 'live'}
		/>
	);
};

export const AudioIconStack = ({ featureList }: AudioIconStackProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { activeFeature } = useAudioPlayerStore();

	const activeFeatureInfo = getFeatureInfo(activeFeature, featureList);

	const primaryFeature = featureList[0];
	const secondaryFeature = featureList[1];
	const isPrimaryActive = activeFeatureInfo?.name === primaryFeature.name;

	// Track if we've ever changed features (to persist animation class)
	const prevFeature = useRef<string | null>(null);
	const hasAnimated = useRef(false);

	// Prevent animation on mount. After the first feature switch,
	// keep animations enabled so reverse transitions still animate
	if (prevFeature.current === null) {
		prevFeature.current = activeFeature;
	} else if (prevFeature.current !== activeFeature) {
		hasAnimated.current = true;
		prevFeature.current = activeFeature;
	}

	const shouldShowAnimation = hasAnimated.current;

	const PrimaryFeatureIcon = primaryFeature.icon;
	const SecondaryFeatureIcon = secondaryFeature.icon;

	return (
		<Box xcss={styles.centeredContainer}>
			<Box xcss={styles.stackContainer} key={activeFeature}>
				<Inline alignInline="center" xcss={styles.stackLayer}>
					{/* Primary tile displays the audio feature icon */}
					<Box
						xcss={cx(
							styles.iconTile,
							isPrimaryActive ? styles.primaryIconTile : styles.primaryIconTileSwapped,
							shouldShowAnimation &&
								(isPrimaryActive
									? styles.primaryIconTileAnimationReversed
									: styles.primaryIconTileAnimation),
						)}
						testId="primary-audio-tile"
					>
						<Box xcss={styles.iconContainer}>
							<Box
								xcss={cx(
									styles.scaledIcon,
									styles.stackedIcon,
									shouldShowAnimation &&
										(isPrimaryActive ? styles.showAnimation : styles.hideAnimation),
									!isPrimaryActive && styles.transparentIconTile,
								)}
								testId="primary-feature-icon"
							>
								<PrimaryFeatureIcon label={formatMessage(primaryFeature.translationString)} />
							</Box>
							<Box
								xcss={cx(
									styles.scaledIcon,
									styles.stackedIcon,
									shouldShowAnimation &&
										(isPrimaryActive ? styles.hideAnimation : styles.showAnimation),
									isPrimaryActive && styles.transparentIconTile,
								)}
								testId="secondary-feature-icon"
							>
								<SecondaryFeatureIcon label={formatMessage(secondaryFeature.translationString)} />
							</Box>
						</Box>
					</Box>

					{/* Secondary tile displays the page emoji/content type icon */}
					<Box
						xcss={cx(
							styles.iconTile,
							isPrimaryActive ? styles.secondaryIconTile : styles.secondaryIconTileSwapped,
							shouldShowAnimation &&
								(isPrimaryActive
									? styles.secondaryIconTileAnimationReversed
									: styles.secondaryIconTileAnimation),
						)}
						testId="secondary-audio-tile"
					>
						<Box xcss={styles.scaledIcon}>
							<AudioEmojiIcon />
						</Box>
					</Box>
				</Inline>
			</Box>
		</Box>
	);
};
